import { getThemePalette } from './theme-palettes';
import { createPaletteChannel } from '../../../theme/styles/utils';

/**
 * Apply theme palette colors to the theme configuration
 */
export function applyThemePalette(
  themeConfig: any,
  paletteId: string,
  mode: 'light' | 'dark'
): any {
  const palette = getThemePalette(paletteId);
  
  if (!palette) {
    console.warn(`Theme palette with id "${paletteId}" not found. Using default palette.`);
    return themeConfig;
  }
  const lightColors = palette.colors.light;
  const darkColors = palette.colors.dark;
  
  // Create palette channels for the new colors
  const lightPrimaryChannel = createPaletteChannel({
    main: lightColors.primary,
    contrastText: '#FFFFFF',
  });
  
  const lightSecondaryChannel = createPaletteChannel({
    main: lightColors.secondary,
    contrastText: '#000000',
  });

  const lightErrorChannel = createPaletteChannel({
    main: lightColors.error,
    contrastText: '#FFFFFF',
  });
  
  const darkPrimaryChannel = createPaletteChannel({
    main: darkColors.primary,
    contrastText: '#FFFFFF',
  });
  
  const darkSecondaryChannel = createPaletteChannel({
    main: darkColors.secondary,
    contrastText: '#000000',
  });

  const darkErrorChannel = createPaletteChannel({
    main: darkColors.error,
    contrastText: '#FFFFFF',
  });
  
  // Create updated theme configuration with new colors for both light and dark modes
  const updatedConfig = {
    ...themeConfig,
    colorSchemes: {
      ...themeConfig.colorSchemes,
      light: {
        ...themeConfig.colorSchemes.light,
        palette: {
          ...themeConfig.colorSchemes.light.palette,
          primary: {
            ...themeConfig.colorSchemes.light.palette.primary,
            ...lightPrimaryChannel,
          },
          secondary: {
            ...themeConfig.colorSchemes.light.palette.secondary,
            ...lightSecondaryChannel,
          },
          error: {
            ...themeConfig.colorSchemes.light.palette.error,
            ...lightErrorChannel,
          },
          background: {
            ...themeConfig.colorSchemes.light.palette.background,
            default: lightColors.background,
            paper: lightColors.paper,
          },
          surface: {
            ...themeConfig.colorSchemes.light.palette.surface,
            main: lightColors.surface,
            card: lightColors.paper,    // canvas bg = card bg
            sidebar: lightColors.paper,
          },
          text: {
            ...themeConfig.colorSchemes.light.palette.text,
            primary: lightColors.text,
          },
        },
      },
      dark: {
        ...themeConfig.colorSchemes.dark,
        palette: {
          ...themeConfig.colorSchemes.dark.palette,
          primary: {
            ...themeConfig.colorSchemes.dark.palette.primary,
            ...darkPrimaryChannel,
          },
          secondary: {
            ...themeConfig.colorSchemes.dark.palette.secondary,
            ...darkSecondaryChannel,
          },
          error: {
            ...themeConfig.colorSchemes.dark.palette.error,
            ...darkErrorChannel,
          },
          background: {
            ...themeConfig.colorSchemes.dark.palette.background,
            default: darkColors.background,
            paper: darkColors.paper,
          },
          surface: {
            ...themeConfig.colorSchemes.dark.palette.surface,
            main: darkColors.surface,
            card: darkColors.paper,    // canvas bg = card bg
            sidebar: darkColors.paper,
          },
          text: {
            ...themeConfig.colorSchemes.dark.palette.text,
            primary: darkColors.text,
          },
        },
      },
    },
  };

  return updatedConfig;
}

/**
 * Generate CSS custom properties for the selected theme palette
 */
export function generateThemePaletteCSS(paletteId: string, mode: 'light' | 'dark'): string {
  const palette = getThemePalette(paletteId);
  
  if (!palette) {
    return '';
  }

  const colors = palette.colors[mode];
  
  return `
    :root {
      --theme-palette-primary: ${colors.primary};
      --theme-palette-secondary: ${colors.secondary};
      --theme-palette-background: ${colors.background};
      --theme-palette-surface: ${colors.surface};
      --theme-palette-error: ${colors.error};
      --theme-palette-paper: ${colors.paper};
      --theme-palette-text: ${colors.text};
    }
  `;
}

/**
 * Get theme palette colors for a specific mode
 */
export function getThemePaletteColors(paletteId: string, mode: 'light' | 'dark') {
  const palette = getThemePalette(paletteId);
  
  if (!palette) {
    return null;
  }

  return palette.colors[mode];
}

/**
 * Check if a theme palette exists
 */
export function isValidThemePalette(paletteId: string): boolean {
  return getThemePalette(paletteId) !== undefined;
}
